import React, { useState, useEffect, useMemo, memo } from 'react';
import JSZip from 'jszip';
import { readXlsxSheetAsRows } from '../../../../lib/xlsxUtils';
import { parseCSV } from '../../../../lib/csvUtils';
import { Tabs, Table, Typography } from '../../../../components';
import { REQUIRED_CSV_FILES, REQUIRED_EXCEL_SHEETS } from '../../../../constants/discovery';

const { Title } = Typography;

const CSVDataViewerComponent = ({ zipOrCSVFile, setStep }) => {
    const [csvData, setCsvData] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(null);

    useEffect(() => {
        const parseCSVFiles = async () => {
            if (!zipOrCSVFile) return;

            setLoading(true);
            try {
                const isExcel = zipOrCSVFile.name.endsWith('.xlsx') || zipOrCSVFile.name.endsWith('.xls');
                const parsedData = isExcel ? await parseExcelFile(zipOrCSVFile) : await parseZipCSVFile(zipOrCSVFile);

                setCsvData(parsedData);
                // Set the first available CSV as the active tab
                const firstFile = Object.keys(parsedData)[0];
                if (firstFile) {
                    setActiveTab(firstFile);
                }
            } catch (error) {
                console.error('Error parsing files:', error);
            } finally {
                // Add a bit of delay for better UX when parsing CSV is fast
                setTimeout(() => {
                    setStep(2);
                    setLoading(false);
                }, 1000);
            }
        };

        parseCSVFiles();
    }, [zipOrCSVFile]);

    const parseZipCSVFile = async (zipFile) => {
        const zip = await JSZip.loadAsync(zipFile);
        const parsedData = {};

        for (const fileName of REQUIRED_CSV_FILES) {
            const zipEntry = Object.values(zip.files).find((f) => f.name.endsWith(fileName));
            if (zipEntry) {
                const csvContent = await zipEntry.async('text');
                parsedData[fileName] = parseCSV(csvContent);
            }
        }
        return parsedData;
    };

    const parseExcelFile = async (excelFile) => {
        const parsedData = {};

        for (const sheetName of REQUIRED_EXCEL_SHEETS) {
            const rows = await readXlsxSheetAsRows(excelFile, sheetName);
            if (!rows) continue;

            const csvString = rows
                .map((row) =>
                    row
                        .map((cell) => {
                            const val = String(cell ?? '');
                            return val.includes(',') || val.includes('"') || val.includes('\n')
                                ? `"${val.replace(/"/g, '""')}"`
                                : val;
                        })
                        .join(','),
                )
                .join('\n');
            parsedData[sheetName] = parseCSV(csvString);
        }
        return parsedData;
    };

    // Memoize tab items to avoid unnecessary recalculations
    const tabItems = useMemo(() => {
        return Object.entries(csvData).map(([fileName, { columns, data }]) => {
            // Calculate total width for horizontal scroll
            const totalWidth = columns.reduce((sum, col) => sum + col.width, 0);
            return {
                key: fileName,
                label: fileName,
                children: (
                    <Table
                        columns={columns}
                        dataSource={data}
                        scroll={{ x: totalWidth, y: 500 }}
                        pagination={false}
                        size="small"
                        tableLayout="fixed"
                        virtual
                    />
                ),
            };
        });
    }, [csvData]);

    if (loading) return;

    if (Object.keys(csvData).length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Title level={4}>No CSV data found</Title>
            </div>
        );
    }

    return (
        <div className="csv-data-viewer">
            <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" items={tabItems} />
        </div>
    );
};

// Memoize the entire component so it only re-renders when props change
export const CSVDataViewer = memo(CSVDataViewerComponent);
